// Empty-state placeholder with icon + heading + explanation + optional CTA.
// Replaces "No items yet" text with something users can actually act on.
//
// Usage:
//   <EmptyState icon="📁" title="No matters yet"
//     description="Matters are the individual cases you work on for clients."
//     action={{ label: '+ New Matter', onClick: () => setShowForm(true) }} />
//
// For pages where filters may be empty (as opposed to nothing existing at
// all), use variant="filtered" instead of the default:
//   <EmptyState variant="filtered" title="No results match your filters"
//     description="Try clearing filters or changing your search terms."
//     action={{ label: 'Clear filters', onClick: handleClearFilters }} />

export default function EmptyState({
  icon = '📄',
  title,
  description,
  action,          // { label, onClick, href } — either onClick OR href
  secondary,       // { label, onClick } — muted secondary action (e.g. "Learn more")
  variant = 'empty',  // 'empty' | 'filtered' | 'error'
  className = '',
}) {
  const iconClass = variant === 'error'
    ? 'text-red-400'
    : variant === 'filtered'
    ? 'text-amber-400'
    : 'text-gray-400'

  return (
    <div
      role="status"
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
    >
      <div className={`text-6xl mb-3 ${iconClass}`} aria-hidden="true">
        {icon}
      </div>
      {title && (
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-sm text-gray-500 max-w-md mb-6">
          {description}
        </p>
      )}
      {action && (
        <div className="flex flex-wrap items-center gap-3 justify-center">
          {action.href ? (
            <a
              href={action.href}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm">
              {action.label}
            </a>
          ) : (
            <button
              onClick={action.onClick}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm">
              {action.label}
            </button>
          )}
          {secondary && (
            <button
              onClick={secondary.onClick}
              className="text-sm text-gray-500 hover:text-gray-700 underline">
              {secondary.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
